import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Star,
  Gift,
  TrendingUp,
  TrendingDown,
  Users,
  RefreshCw,
  Settings,
  Calendar,
  DollarSign,
  Activity,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  FileText,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatear precio
const formatPrice = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Formatear fecha
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

export default function PuntosJPMAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [topClientes, setTopClientes] = useState([]);
  const [ultimosMovimientos, setUltimosMovimientos] = useState([]);
  const [reporteMensual, setReporteMensual] = useState(null);
  const [reporteHistorico, setReporteHistorico] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Obtener configuración
      const configRes = await axios.get(`${API_URL}/api/puntos-jpm/config`, { headers });
      setConfig(configRes.data);

      // Obtener estadísticas
      const statsRes = await axios.get(`${API_URL}/api/puntos-jpm/estadisticas`, { headers });
      setStats(statsRes.data);

      // Obtener top clientes
      const topRes = await axios.get(`${API_URL}/api/puntos-jpm/top-clientes?limit=10`, { headers });
      setTopClientes(topRes.data.clientes || []);

      // Obtener últimos movimientos
      const movsRes = await axios.get(`${API_URL}/api/puntos-jpm/ultimos-movimientos?limit=15`, { headers });
      setUltimosMovimientos(movsRes.data.movimientos || []);
      
      // Obtener reporte mensual actual
      const reporteRes = await axios.get(`${API_URL}/api/puntos-jpm/reporte-mensual`, { headers });
      setReporteMensual(reporteRes.data);
      
      // Obtener reporte histórico
      const historicoRes = await axios.get(`${API_URL}/api/puntos-jpm/reporte-historico?meses=6`, { headers });
      setReporteHistorico(historicoRes.data.meses || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos del programa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/puntos-jpm/sincronizar-ventas-elegibles`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.mensaje);
      fetchData();
    } catch (error) {
      toast.error('Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Star className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
                <h1 className="text-xl font-display">Programa Puntos JPM</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReporteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-sm transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Reporte Mensual
              </button>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-sm transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Sincronizar
              </button>
              <button
                onClick={() => setShowConfigModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#C49F27] text-black rounded-lg text-sm font-medium transition-colors"
              >
                <Settings className="w-4 h-4" />
                Configuración
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estado del programa */}
        <div className="mb-6 flex items-center gap-3">
          {config?.estado === 'activo' ? (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              Programa Activo
            </span>
          ) : (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              <AlertCircle className="w-4 h-4" />
              Programa Inactivo
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            Desde: {formatDate(config?.fecha_activacion)}
          </span>
          <span className="text-sm text-muted-foreground">
            • {config?.valor_para_punto?.toLocaleString('es-CO')} COP = 1 punto
          </span>
          <span className="text-sm text-muted-foreground">
            • 1 punto = ${config?.valor_redencion_punto} COP
          </span>
        </div>

        {/* Métricas principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Puntos Emitidos */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Puntos Emitidos</span>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-emerald-600">
              {(stats?.total_emitidos || 0).toLocaleString('es-CO')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Valor: {formatPrice((stats?.total_emitidos || 0) * (config?.valor_redencion_punto || 100))}
            </p>
          </div>

          {/* Puntos Redimidos */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Puntos Redimidos</span>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-blue-600">
              {(stats?.total_redimidos || 0).toLocaleString('es-CO')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Valor: {formatPrice((stats?.total_redimidos || 0) * (config?.valor_redencion_punto || 100))}
            </p>
          </div>

          {/* Puntos Activos */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Puntos Activos</span>
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#D4AF37]">
              {(stats?.total_activos || 0).toLocaleString('es-CO')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Pasivo: {formatPrice((stats?.total_activos || 0) * (config?.valor_redencion_punto || 100))}
            </p>
          </div>

          {/* Clientes con Puntos */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Clientes con Puntos</span>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-purple-600">
              {(stats?.clientes_con_puntos || 0).toLocaleString('es-CO')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Promedio: {stats?.clientes_con_puntos ? Math.round((stats?.total_activos || 0) / stats.clientes_con_puntos) : 0} pts/cliente
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Clientes */}
          <div className="bg-white rounded-xl border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Users className="w-5 h-5 text-[#D4AF37]" />
                Top Clientes con Puntos
              </h3>
            </div>
            <div className="p-4">
              {topClientes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hay clientes con puntos aún
                </p>
              ) : (
                <div className="space-y-3">
                  {topClientes.map((cliente, idx) => (
                    <div
                      key={cliente.cliente_id}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-[#D4AF37] text-black' :
                          idx === 1 ? 'bg-zinc-400 text-white' :
                          idx === 2 ? 'bg-amber-700 text-white' :
                          'bg-zinc-200 text-zinc-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{cliente.nombre}</p>
                          <p className="text-xs text-muted-foreground">{cliente.cliente_id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#D4AF37]">{cliente.saldo?.toLocaleString('es-CO')} pts</p>
                        <p className="text-xs text-muted-foreground">
                          {formatPrice(cliente.saldo * (config?.valor_redencion_punto || 100))}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Últimos Movimientos */}
          <div className="bg-white rounded-xl border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#D4AF37]" />
                Últimos Movimientos
              </h3>
            </div>
            <div className="p-4 max-h-[400px] overflow-y-auto">
              {ultimosMovimientos.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hay movimientos aún
                </p>
              ) : (
                <div className="space-y-2">
                  {ultimosMovimientos.map((mov, idx) => (
                    <div
                      key={mov.id || idx}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          mov.tipo === 'acumulacion' ? 'bg-emerald-100' :
                          mov.tipo === 'redencion' ? 'bg-blue-100' :
                          mov.tipo === 'reversion' ? 'bg-red-100' :
                          'bg-zinc-100'
                        }`}>
                          {mov.tipo === 'acumulacion' ? (
                            <TrendingUp className="w-4 h-4 text-emerald-600" />
                          ) : mov.tipo === 'redencion' ? (
                            <Gift className="w-4 h-4 text-blue-600" />
                          ) : mov.tipo === 'reversion' ? (
                            <TrendingDown className="w-4 h-4 text-red-600" />
                          ) : (
                            <Settings className="w-4 h-4 text-zinc-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium capitalize">{mov.tipo?.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(mov.fecha)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          mov.puntos > 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {mov.puntos > 0 ? '+' : ''}{mov.puntos?.toLocaleString('es-CO')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {mov.venta_numero || mov.cliente_id?.slice(0, 8)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reporte del Mes Actual */}
        {reporteMensual && (
          <div className="mt-8 bg-white rounded-xl border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                Reporte de {reporteMensual.periodo?.nombre_mes} {reporteMensual.periodo?.año}
              </h3>
              <button
                onClick={() => setShowReporteModal(true)}
                className="text-sm text-[#D4AF37] hover:underline"
              >
                Ver detalle completo
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-emerald-600 mb-1">Emitidos</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {reporteMensual.resumen?.puntos_emitidos?.toLocaleString('es-CO')}
                  </p>
                  <p className="text-xs text-emerald-600">
                    {formatPrice(reporteMensual.valor_cop?.emitidos)}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-blue-600 mb-1">Redimidos</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {reporteMensual.resumen?.puntos_redimidos?.toLocaleString('es-CO')}
                  </p>
                  <p className="text-xs text-blue-600">
                    {formatPrice(reporteMensual.valor_cop?.redimidos)}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-red-600 mb-1">Revertidos</p>
                  <p className="text-2xl font-bold text-red-700">
                    {reporteMensual.resumen?.puntos_revertidos?.toLocaleString('es-CO')}
                  </p>
                  <p className="text-xs text-red-600">
                    {formatPrice(reporteMensual.valor_cop?.revertidos)}
                  </p>
                </div>
                <div className="bg-[#D4AF37]/10 rounded-lg p-4 text-center">
                  <p className="text-xs text-[#D4AF37] mb-1">Neto del Mes</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">
                    {reporteMensual.resumen?.neto_periodo?.toLocaleString('es-CO')}
                  </p>
                  <p className="text-xs text-[#D4AF37]">
                    {formatPrice(reporteMensual.valor_cop?.neto)}
                  </p>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-center gap-8 text-sm text-muted-foreground">
                <span>{reporteMensual.clientes?.acumularon || 0} clientes acumularon</span>
                <span>•</span>
                <span>{reporteMensual.clientes?.redimieron || 0} clientes redimieron</span>
              </div>
            </div>
          </div>
        )}

        {/* Histórico de Últimos 6 Meses */}
        {reporteHistorico.length > 0 && (
          <div className="mt-8 bg-white rounded-xl border border-border">
            <div className="p-4 border-b border-border">
              <h3 className="font-medium flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
                Histórico Últimos 6 Meses
              </h3>
            </div>
            <div className="p-4">
              <div className="flex items-end justify-between gap-2 h-40">
                {reporteHistorico.map((mes, idx) => {
                  const maxValue = Math.max(...reporteHistorico.map(m => Math.max(m.emitidos, m.redimidos)));
                  const emitidosHeight = maxValue > 0 ? (mes.emitidos / maxValue) * 100 : 0;
                  const redimidosHeight = maxValue > 0 ? (mes.redimidos / maxValue) * 100 : 0;
                  
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div className="flex items-end gap-1 h-32 w-full justify-center">
                        <div 
                          className="w-4 bg-emerald-400 rounded-t transition-all"
                          style={{ height: `${emitidosHeight}%`, minHeight: mes.emitidos > 0 ? '4px' : '0' }}
                          title={`Emitidos: ${mes.emitidos}`}
                        />
                        <div 
                          className="w-4 bg-blue-400 rounded-t transition-all"
                          style={{ height: `${redimidosHeight}%`, minHeight: mes.redimidos > 0 ? '4px' : '0' }}
                          title={`Redimidos: ${mes.redimidos}`}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{mes.nombre}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-400 rounded" />
                  <span className="text-xs text-muted-foreground">Emitidos</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-400 rounded" />
                  <span className="text-xs text-muted-foreground">Redimidos</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de Configuración */}
      {showConfigModal && (
        <ConfigModal
          config={config}
          onClose={() => setShowConfigModal(false)}
          onSave={(newConfig) => {
            setConfig(newConfig);
            setShowConfigModal(false);
            toast.success('Configuración actualizada');
          }}
        />
      )}

      {/* Modal de Reporte Mensual Detallado */}
      {showReporteModal && reporteMensual && (
        <ReporteModal
          reporte={reporteMensual}
          config={config}
          onClose={() => setShowReporteModal(false)}
        />
      )}
    </div>
  );
}

// Modal de Configuración
function ConfigModal({ config, onClose, onSave }) {
  const [formData, setFormData] = useState({
    estado: config?.estado || 'activo',
    fecha_activacion: config?.fecha_activacion?.split('T')[0] || '',
    valor_para_punto: config?.valor_para_punto || 10000,
    valor_redencion_punto: config?.valor_redencion_punto || 100
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/puntos-jpm/config`,
        {
          estado: formData.estado,
          fecha_activacion: formData.fecha_activacion ? `${formData.fecha_activacion}T00:00:00Z` : null,
          valor_para_punto: parseInt(formData.valor_para_punto),
          valor_redencion_punto: parseInt(formData.valor_redencion_punto)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSave(response.data);
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#D4AF37]" />
            Configuración Puntos JPM
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-black">
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Estado */}
          <div>
            <label className="block text-sm font-medium mb-2">Estado del Programa</label>
            <select
              value={formData.estado}
              onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value }))}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>

          {/* Fecha de activación */}
          <div>
            <label className="block text-sm font-medium mb-2">Fecha de Activación</label>
            <input
              type="date"
              value={formData.fecha_activacion}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_activacion: e.target.value }))}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-[#D4AF37]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Solo ventas creadas después de esta fecha generan puntos
            </p>
          </div>

          {/* Valor para punto */}
          <div>
            <label className="block text-sm font-medium mb-2">COP para generar 1 punto</label>
            <input
              type="number"
              value={formData.valor_para_punto}
              onChange={(e) => setFormData(prev => ({ ...prev, valor_para_punto: e.target.value }))}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-[#D4AF37]"
            />
          </div>

          {/* Valor redención */}
          <div>
            <label className="block text-sm font-medium mb-2">Valor de redención por punto (COP)</label>
            <input
              type="number"
              value={formData.valor_redencion_punto}
              onChange={(e) => setFormData(prev => ({ ...prev, valor_redencion_punto: e.target.value }))}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-[#D4AF37]"
            />
          </div>

          {/* Resumen */}
          <div className="bg-zinc-50 rounded-lg p-4">
            <p className="text-sm font-medium mb-2">Resumen del beneficio</p>
            <p className="text-xs text-muted-foreground">
              Por cada ${parseInt(formData.valor_para_punto || 0).toLocaleString('es-CO')} COP pagados = 1 punto
            </p>
            <p className="text-xs text-muted-foreground">
              1 punto = ${parseInt(formData.valor_redencion_punto || 0)} COP de beneficio
            </p>
            <p className="text-xs text-[#D4AF37] mt-1 font-medium">
              Beneficio: {((parseInt(formData.valor_redencion_punto || 0) / parseInt(formData.valor_para_punto || 1)) * 100).toFixed(1)}% del valor pagado
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-zinc-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-[#D4AF37] hover:bg-[#C49F27] text-black font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Modal de Reporte Mensual Detallado
function ReporteModal({ reporte, config, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl my-8">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            Reporte de {reporte.periodo?.nombre_mes} {reporte.periodo?.año}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-black">
            ✕
          </button>
        </div>
        
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Resumen principal */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-emerald-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <span className="text-sm text-emerald-700">Puntos Emitidos</span>
              </div>
              <p className="text-3xl font-bold text-emerald-700">
                {reporte.resumen?.puntos_emitidos?.toLocaleString('es-CO')}
              </p>
              <p className="text-sm text-emerald-600 mt-1">
                {formatPrice(reporte.valor_cop?.emitidos)}
              </p>
              <p className="text-xs text-emerald-500 mt-2">
                {reporte.resumen?.movimientos_emision} movimientos
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">Puntos Redimidos</span>
              </div>
              <p className="text-3xl font-bold text-blue-700">
                {reporte.resumen?.puntos_redimidos?.toLocaleString('es-CO')}
              </p>
              <p className="text-sm text-blue-600 mt-1">
                {formatPrice(reporte.valor_cop?.redimidos)}
              </p>
              <p className="text-xs text-blue-500 mt-2">
                {reporte.resumen?.movimientos_redencion} redenciones
              </p>
            </div>
          </div>
          
          {/* Neto y reversiones */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700">Reversiones</span>
              </div>
              <p className="text-2xl font-bold text-red-700">
                {reporte.resumen?.puntos_revertidos?.toLocaleString('es-CO')}
              </p>
              <p className="text-sm text-red-600 mt-1">
                {formatPrice(reporte.valor_cop?.revertidos)}
              </p>
            </div>
            
            <div className="bg-[#D4AF37]/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                <span className="text-sm text-[#D4AF37]">Neto del Período</span>
              </div>
              <p className="text-2xl font-bold text-[#D4AF37]">
                {reporte.resumen?.neto_periodo > 0 ? '+' : ''}{reporte.resumen?.neto_periodo?.toLocaleString('es-CO')}
              </p>
              <p className="text-sm text-[#D4AF37] mt-1">
                {formatPrice(reporte.valor_cop?.neto)}
              </p>
            </div>
          </div>
          
          {/* Clientes */}
          <div className="bg-zinc-50 rounded-xl p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-zinc-600" />
              Participación de Clientes
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold">{reporte.clientes?.acumularon || 0}</p>
                <p className="text-sm text-muted-foreground">clientes acumularon puntos</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{reporte.clientes?.redimieron || 0}</p>
                <p className="text-sm text-muted-foreground">clientes redimieron puntos</p>
              </div>
            </div>
          </div>
          
          {/* Datos diarios */}
          {reporte.datos_diarios?.length > 0 && (
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-zinc-600" />
                Detalle Diario
              </h4>
              <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Fecha</th>
                      <th className="text-right p-2 font-medium text-emerald-600">Emitidos</th>
                      <th className="text-right p-2 font-medium text-blue-600">Redimidos</th>
                      <th className="text-right p-2 font-medium text-red-600">Revertidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporte.datos_diarios.map((dia, idx) => (
                      <tr key={idx} className="border-t border-border">
                        <td className="p-2">{dia.fecha}</td>
                        <td className="p-2 text-right text-emerald-600">
                          {dia.acumulacion > 0 ? `+${dia.acumulacion}` : '-'}
                        </td>
                        <td className="p-2 text-right text-blue-600">
                          {dia.redencion > 0 ? `-${dia.redencion}` : '-'}
                        </td>
                        <td className="p-2 text-right text-red-600">
                          {dia.reversion > 0 ? `-${dia.reversion}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
